// src/components/QuestionHistory.js
import React, { useState, useEffect } from 'react';
import { questionHistoryAPI } from '../api/api';

const QuestionHistory = ({ questionId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    if (questionId) {
      loadHistory();
    }
  }, [questionId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await questionHistoryAPI.getByQuestionId(questionId);
      setHistory(data);
    } catch (err) {
      setError('Erreur lors du chargement de l\'historique');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeType = (oldContent, newContent) => {
    try {
      // Convertir en chaînes si nécessaire
      let oldStr = typeof oldContent === 'string' ? oldContent : (oldContent ? JSON.stringify(oldContent) : '');
      let newStr = typeof newContent === 'string' ? newContent : (newContent ? JSON.stringify(newContent) : '');

      // Vérifier si oldContent est une chaîne vide ou null/undefined
      if (!oldContent || oldStr === '{}' || oldStr.trim() === '') {
        return 'Création';
      }

      // Vérifier si newContent est une chaîne vide ou null/undefined
      if (!newContent || newStr === '{}' || newStr.trim() === '') {
        return 'Suppression';
      }

      // Parser les contenus s'ils sont valides
      let parsedOld, parsedNew;
      try {
        parsedOld = JSON.parse(oldStr);
      } catch {
        parsedOld = null;
      }

      try {
        parsedNew = JSON.parse(newStr);
      } catch {
        parsedNew = null;
      }

      // Vérifier si les objets parsés sont vides
      if (!parsedOld || (typeof parsedOld === 'object' && Object.keys(parsedOld).length === 0)) {
        return 'Création';
      }

      if (!parsedNew || (typeof parsedNew === 'object' && Object.keys(parsedNew).length === 0)) {
        return 'Suppression';
      }

      return 'Modification';
    } catch (error) {
      console.error('Error in getChangeType:', error);
      // Default fallback
      if (!oldContent) {
        return 'Création';
      } else if (!newContent) {
        return 'Suppression';
      } else {
        return 'Modification';
      }
    }
  };

  const isValidJsonAndNotEmpty = (jsonString) => {
    try {
      // Vérifier si c'est une chaîne vide ou une représentation d'objet vide
      if (!jsonString) {
        return false;
      }

      // Si ce n'est pas une chaîne, le convertir en chaîne
      let jsonStr = typeof jsonString === 'string' ? jsonString : JSON.stringify(jsonString);

      if (jsonStr === '{}' || jsonStr.trim() === '') {
        return false;
      }

      const parsed = JSON.parse(jsonStr);
      return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
    } catch (error) {
      console.error('Error parsing JSON in isValidJsonAndNotEmpty:', error);
      return false;
    }
  };

  const getChangedFields = (oldContent, newContent) => {
    if (!oldContent || !newContent) return [];

    try {
      const changes = [];

      // Convertir en objets si nécessaire
      let oldObj, newObj;
      try {
        let oldStr = typeof oldContent === 'string' ? oldContent : JSON.stringify(oldContent);
        oldObj = JSON.parse(oldStr);
      } catch {
        oldObj = {};
      }

      try {
        let newStr = typeof newContent === 'string' ? newContent : JSON.stringify(newContent);
        newObj = JSON.parse(newStr);
      } catch {
        newObj = {};
      }

      // Vérifier que ce sont bien des objets
      if (typeof oldObj !== 'object' || typeof newObj !== 'object' || oldObj === null || newObj === null) {
        return changes;
      }

      // Obtenir toutes les clés uniques
      const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

      allKeys.forEach(key => {
        const oldValue = oldObj[key];
        const newValue = newObj[key];

        // Comparer les valeurs
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push(key);
        }
      });

      return changes;
    } catch (error) {
      console.error('Error in getChangedFields:', error);
      return [];
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Historique des modifications</h3>
      
      {history.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          Aucune modification enregistrée pour cette question
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {history.map((entry, index) => {
              const changeType = getChangeType(entry.ancien_contenu, entry.nouveau_contenu);
              const changedFields = getChangedFields(entry.ancien_contenu, entry.nouveau_contenu);
              
              return (
                <li key={entry.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className={`w-3 h-3 rounded-full ${
                        changeType === 'Création' ? 'bg-green-500' : 
                        changeType === 'Suppression' ? 'bg-red-500' : 'bg-blue-500'
                      }`}></div>
                    </div>
                    
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            changeType === 'Création' ? 'bg-green-100 text-green-800' : 
                            changeType === 'Suppression' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {changeType}
                          </span>
                          
                          {changedFields.length > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              {changedFields.join(', ')}
                            </span>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          {formatDate(entry.modifie_le)}
                        </div>
                      </div>
                      
                      <div className="mt-1 text-sm text-gray-600">
                        {entry.modifie_par_email ? (
                          <span>Par {entry.modifie_par_email.split('@')[0]}</span>
                        ) : (
                          <span>Par utilisateur inconnu</span>
                        )}
                      </div>
                      
                      {/* Bouton pour afficher les détails */}
                      <div className="mt-2">
                        <button
                          onClick={() => setSelectedVersion(selectedVersion === entry.id ? null : entry.id)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {selectedVersion === entry.id ? 'Masquer les détails' : 'Afficher les détails'}
                        </button>
                        
                        {selectedVersion === entry.id && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-gray-700 mb-1">Avant</h4>
                                <pre className="bg-white p-2 rounded border text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                  {entry.ancien_contenu && isValidJsonAndNotEmpty(entry.ancien_contenu)
                                    ? JSON.stringify(typeof entry.ancien_contenu === 'string' ? JSON.parse(entry.ancien_contenu) : entry.ancien_contenu, null, 2)
                                    : 'Aucun contenu'}
                                </pre>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-700 mb-1">Après</h4>
                                <pre className="bg-white p-2 rounded border text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                  {entry.nouveau_contenu && isValidJsonAndNotEmpty(entry.nouveau_contenu)
                                    ? JSON.stringify(typeof entry.nouveau_contenu === 'string' ? JSON.parse(entry.nouveau_contenu) : entry.nouveau_contenu, null, 2)
                                    : 'Aucun contenu'}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default QuestionHistory;